"use client";
import React from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/admin/ui/input";
import { Select } from "@/components/admin/ui/select";
import { Checkbox } from "@/components/admin/ui/checkbox";
import { Button } from "@/components/admin/ui/button";
import { DateField } from "@/components/admin/ui/date-field";

export type FilterDef =
  | { key: string; label: string; type: 'text'; placeholder?: string }
  | { key: string; label: string; type: 'date'; placeholder?: string }
  | { key: string; label: string; type: 'select'; options: { value: string; label: string }[]; placeholder?: string }
  | { key: string; label: string; type: 'boolean'; trueLabel?: string; falseLabel?: string };

type Props = {
  filters: FilterDef[];
  persistKey?: string; // defaults to pathname
  enableCopyLink?: boolean;
};

export function FiltersBar({ filters, persistKey, enableCopyLink = true }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const pkey = persistKey || pathname || '/admin';
  const initialized = React.useRef(false);

  // On first mount, restore saved filters if URL lacks all filter keys
  React.useEffect(() => {
    if (initialized.current) return;
    try {
      const haveAny = filters.some(f => (sp?.get(f.key) ?? '') !== '');
      if (!haveAny && typeof window !== 'undefined') {
        const raw = localStorage.getItem(`filters:${pkey}`);
        if (raw) {
          const saved = JSON.parse(raw) as Record<string, string>;
          const next = new URLSearchParams(sp?.toString() || '');
          for (const f of filters) {
            const val = saved[f.key];
            if (val != null && val !== '') next.set(f.key, String(val));
          }
          router.replace(`${pathname}?${next.toString()}`);
        }
      }
    } catch {}
    finally { initialized.current = true; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setParam(key: string, value?: string) {
    const next = new URLSearchParams(sp?.toString() || '');
    if (value && value.length > 0) next.set(key, value); else next.delete(key);
    router.replace(`${pathname}?${next.toString()}`);
    try {
      const saved: Record<string, string> = JSON.parse(localStorage.getItem(`filters:${pkey}`) || '{}');
      if (value && value.length > 0) saved[key] = value; else delete saved[key];
      localStorage.setItem(`filters:${pkey}`, JSON.stringify(saved));
    } catch {}
  }

  return (
    <div className="admin-toolbar flex flex-wrap items-end gap-2 p-2 bg-muted/20">
      {filters.map((f) => {
        const value = sp?.get(f.key) ?? '';
        if (f.type === 'text') {
          return (
            <div key={f.key} className="flex flex-col gap-0.5">
              <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">{f.label}</label>
              <Input
                className="h-7 min-w-[10rem] text-xs px-2"
                placeholder={f.placeholder || ''}
                defaultValue={value}
                onBlur={(e) => setParam(f.key, (e.currentTarget as HTMLInputElement).value.trim())}
              />
            </div>
          );
        }
        if (f.type === 'date') {
          return (
            <div key={f.key} className="flex flex-col gap-0.5">
              <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">{f.label}</label>
              <DateField
                value={value}
                onChange={(val) => setParam(f.key, val || '')}
                className="h-7 min-w-[9rem] text-xs px-2"
              />
            </div>
          );
        }
        if (f.type === 'select') {
          return (
            <div key={f.key} className="flex flex-col gap-0.5">
              <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">{f.label}</label>
              <Select
                className="h-7 min-w-[9rem] text-xs px-2"
                value={value}
                onChange={(e) => setParam(f.key, (e.currentTarget as HTMLSelectElement).value)}
              >
                <option value="">{f.placeholder || 'Any'}</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </div>
          );
        }
        if (f.type === 'boolean') {
          const checked = value === '1';
          return (
            <label key={f.key} className="flex items-center gap-1.5 text-xs pt-3">
              <Checkbox
                checked={checked}
                onChange={(e) => setParam(f.key, (e.currentTarget as HTMLInputElement).checked ? '1' : '')}
              />
              <span className="text-muted-foreground">{f.label}</span>
            </label>
          );
        }
        return null;
      })}
      <div className="ml-auto" />
      {enableCopyLink && (
        <Button
          className="h-7 ml-auto text-[10px] px-2 uppercase tracking-wide font-medium"
          onClick={async () => { try { await navigator.clipboard.writeText(window.location.href); } catch {} }}
          aria-label="Copy link"
          title="Copy link"
        >Copy Link</Button>
      )}
    </div>
  );
}

export default FiltersBar;
